import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CreateSavedViewResponse, CreateSavedViewVariables} from '../components/views/types'
import {parseFetchErrorMessage, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries/saved-views'

interface UseCreateSavedViewMutationOptions {
  owner: string
  repo: string
}

// Primes the single-view cache so the post-create redirect renders immediately, then invalidates the list.
export function useCreateSavedViewMutation({owner, repo}: UseCreateSavedViewMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<CreateSavedViewResponse, Error, CreateSavedViewVariables>({
    mutationFn: async variables => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views.json`, {
        method: 'POST',
        body: variables,
      })
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, `Failed to create view (${response.status})`))
      }
      return (await response.json()) as CreateSavedViewResponse
    },
    onSuccess: ({savedView}) => {
      queryClient.setQueryData([SAVED_VIEW_QUERY_KEY, owner, repo, String(savedView.id)] as const, {savedView})
      void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
    },
  })
}
